import {
  ELIGIBILITY_REASONS,
  validatePatientIntake,
  type CaseForPharmacy,
  type EligibilityReason,
  type PatientData,
  type PatientFieldError,
} from "@omony/domain";
import { Banner, Button, Checkbox, Segmented, Switch, TextField } from "@omony/ui";
import { useMutation } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ErrorBanner } from "../../shared/auth/screens.js";
import { api, ApiError } from "../../shared/api.js";
import { queryClient } from "../../shared/query.js";
import { applyCaseResponse, useFeatures } from "../data.js";
import { PatientLanguage } from "../PatientLanguage.js";
import { CardScanner, type ScanResult } from "./CardScanner.js";
import { Spacer, StepFrame, useT } from "./common.js";
import styles from "./flow.module.css";

type Field = keyof PatientData;
type Source = "vision" | "lokal_ocr" | "manuell";

interface FormState {
  patient: PatientData;
  eligibility: EligibilityReason[];
  consent: boolean;
  insuranceType: "gkv" | "pkv";
  doctorSource: "omony_netz" | "lokal";
  knownPatient: boolean;
  captureSource: Source;
}

const EMPTY: PatientData = {
  vorname: "",
  name: "",
  strasse: "",
  plz: "",
  ort: "",
  kvnr: "",
  krankenkasse: "",
  ik: "",
  geburtsdatum: "",
  telefon: "",
};

/** Feldreihenfolge, Breite (von 6 Spalten) und Tastatur-Hinweise für die Bildschirmtastatur */
const FIELDS: Array<{
  id: Field;
  span: 2 | 3 | 4 | 6;
  autoComplete?: string;
  inputMode?: "numeric" | "tel" | "text";
  code?: boolean;
  optional?: boolean;
  type?: "tel";
}> = [
  { id: "vorname", span: 3, autoComplete: "given-name" },
  { id: "name", span: 3, autoComplete: "family-name" },
  { id: "geburtsdatum", span: 2, inputMode: "numeric", autoComplete: "bday" },
  { id: "strasse", span: 4, autoComplete: "address-line1" },
  { id: "plz", span: 2, inputMode: "numeric", autoComplete: "postal-code" },
  { id: "ort", span: 4, autoComplete: "address-level2" },
  { id: "krankenkasse", span: 3 },
  { id: "kvnr", span: 3, code: true },
  { id: "ik", span: 3, inputMode: "numeric", code: true, optional: true },
  { id: "telefon", span: 3, inputMode: "tel", type: "tel", autoComplete: "tel", optional: true },
];

const SPAN = { 2: styles.span2, 3: styles.span3, 4: styles.span4, 6: styles.span6 } as const;

/** Geburtsdatum beim Tippen formatieren: 12081964 → 12.08.1964 */
export function formatBirthInput(v: string) {
  if (/[^\d.]/.test(v) || v.includes(".")) return v.slice(0, 10);
  const d = v.slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4)].filter(Boolean).join(".");
}

function initial(c: CaseForPharmacy): FormState {
  const d = (c.draft ?? null) as Partial<FormState> | null;
  const base: FormState = {
    patient: { ...EMPTY, ...(c.patient ?? {}) },
    eligibility: [...c.eligibility],
    consent: !!c.consentAt,
    insuranceType: c.insuranceType,
    doctorSource: c.doctorSource,
    knownPatient: c.knownPatient,
    captureSource: c.patientCaptureSource ?? "manuell",
  };
  if (!d || typeof d !== "object" || c.patient) return base;
  return {
    ...base,
    ...d,
    patient: { ...EMPTY, ...(d.patient ?? {}) },
    eligibility: Array.isArray(d.eligibility) ? d.eligibility : [],
    // Einwilligung wird nie aus dem Entwurf übernommen – sie muss aktiv erteilt werden
    consent: false,
  };
}

export function PatientStep({ c, onSaved }: { c: CaseForPharmacy; onSaved: () => void }) {
  const t = useT();
  const features = useFeatures();
  const [form, setForm] = useState<FormState>(() => initial(c));
  const [errors, setErrors] = useState<Partial<Record<Field, PatientFieldError>>>({});
  const [flagged, setFlagged] = useState<Set<Field>>(new Set());
  const [extra, setExtra] = useState({ eligibility: false, consent: false });
  const dirty = useRef(false);

  // Autosave (800 ms) – nur Entwurf, ohne Einwilligung
  useEffect(() => {
    if (!dirty.current) return;
    const id = window.setTimeout(() => {
      const { consent: _c, ...draft } = form;
      void api<{ version: number }>(`/cases/${c.id}/draft`, {
        method: "PUT",
        body: { draft },
        background: true,
      })
        .then((r) =>
          queryClient.setQueryData<CaseForPharmacy>(["case", c.id], (old) =>
            old ? { ...old, version: r.version } : old,
          ),
        )
        .catch(() => undefined);
    }, 800);
    return () => window.clearTimeout(id);
  }, [form, c.id]);

  const update = (patch: Partial<FormState>) => {
    dirty.current = true;
    setForm((f) => ({ ...f, ...patch }));
  };
  const setField = (id: Field, v: string) => {
    const value = id === "kvnr" ? v.toUpperCase() : id === "geburtsdatum" ? formatBirthInput(v) : v;
    update({ patient: { ...form.patient, [id]: value } });
    if (errors[id]) setErrors((e) => ({ ...e, [id]: undefined }));
  };
  const validateField = (id: Field) => {
    const v = validatePatientIntake({ patient: form.patient, eligibility: ["a"], consent: true });
    setErrors((e) => ({ ...e, [id]: v.fields[id] }));
  };

  const onScan = (r: ScanResult) => {
    const patch: Partial<PatientData> = {};
    const hit = new Set<Field>();
    for (const [k, v] of Object.entries(r.fields) as Array<[Field, string]>) {
      if (v && k in EMPTY) {
        patch[k] = v;
        hit.add(k);
      }
    }
    update({ patient: { ...form.patient, ...patch }, captureSource: r.source });
    setFlagged(hit);
  };

  const save = useMutation({
    mutationFn: () =>
      api(`/cases/${c.id}/patient`, {
        method: "PUT",
        body: {
          patient: form.patient,
          eligibility: form.eligibility,
          consent: form.consent,
          insuranceType: form.insuranceType,
          doctorSource: form.doctorSource,
          knownPatient: form.knownPatient,
          captureSource: form.captureSource,
        },
      }),
    onSuccess: (r) => {
      applyCaseResponse(c.id, r);
      onSaved();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "patient_invalid")
        setErrors((e.extra.fields ?? {}) as Partial<Record<Field, PatientFieldError>>);
    },
  });

  const submit = () => {
    const v = validatePatientIntake(form);
    setErrors(v.fields);
    setExtra({ eligibility: v.eligibilityMissing, consent: v.consentMissing });
    if (v.valid) save.mutate();
    else document.querySelector<HTMLInputElement>("[aria-invalid=true]")?.focus();
  };

  const toggleEligibility = (r: EligibilityReason, on: boolean) => {
    const s = new Set(form.eligibility);
    if (on) s.add(r);
    else s.delete(r);
    update({ eligibility: ELIGIBILITY_REASONS.filter((x) => s.has(x)) });
    setExtra((x) => ({ ...x, eligibility: false }));
  };

  return (
    <StepFrame
      eyebrow={t("console.step.patient")}
      title={t("console.patient.title")}
      lead={t("console.patient.lead")}
      actions={
        <>
          <Spacer />
          <Button
            size="lg"
            icon={<ArrowRight className="omony-mirror" />}
            onPress={submit}
            isDisabled={save.isPending}
          >
            {t("console.patient.submit")}
          </Button>
        </>
      }
    >
      <div className={styles.split}>
        <CardScanner vision={!!features.data?.ocrVision} onResult={onScan} />
        <div className={styles.section}>
          {flagged.size ? <Banner tone="warning">{t("console.scan.check")}</Banner> : null}
          <div className={styles.fields}>
            {FIELDS.map((f) => (
              <TextField
                key={f.id}
                className={`${SPAN[f.span]} ${flagged.has(f.id) ? styles.check : ""}`}
                label={t(`console.patient.field.${f.id}`)}
                description={
                  f.id === "geburtsdatum"
                    ? t("console.patient.hint.geburtsdatum")
                    : f.optional
                      ? t("common.optional")
                      : undefined
                }
                value={form.patient[f.id]}
                onChange={(v) => setField(f.id, v)}
                onBlur={() => validateField(f.id)}
                errorMessage={errors[f.id] ? t(`console.patient.error.${errors[f.id]}`) : undefined}
                autoComplete={f.autoComplete ?? "off"}
                inputMode={f.inputMode}
                type={f.type}
                code={f.code}
                isRequired={!f.optional}
                enterKeyHint="next"
                onFocus={(e) =>
                  (e.target as HTMLElement).scrollIntoView({ block: "center", behavior: "smooth" })
                }
              />
            ))}
          </div>
          <div className={styles.grid2}>
            <div className={styles.section}>
              <span className={styles.sectionTitle}>{t("console.patient.insurance")}</span>
              <Segmented
                label={t("console.patient.insurance")}
                value={form.insuranceType}
                onChange={(v) => update({ insuranceType: v as "gkv" | "pkv" })}
                items={[
                  { id: "gkv", label: t("console.patient.gkv") },
                  { id: "pkv", label: t("console.patient.pkv") },
                ]}
              />
            </div>
            <div className={styles.section}>
              <span className={styles.sectionTitle}>{t("console.patient.doctorSource")}</span>
              <Segmented
                label={t("console.patient.doctorSource")}
                value={form.doctorSource}
                onChange={(v) => update({ doctorSource: v as "omony_netz" | "lokal" })}
                items={[
                  { id: "omony_netz", label: t("console.patient.doctor.omony_netz") },
                  { id: "lokal", label: t("console.patient.doctor.lokal") },
                ]}
              />
            </div>
          </div>
          <Switch isSelected={form.knownPatient} onChange={(v) => update({ knownPatient: v })}>
            {t("console.patient.known")}
          </Switch>
          {form.insuranceType === "pkv" ? <Banner tone="info">{t("console.patient.pkvHint")}</Banner> : null}
        </div>
      </div>

      <p className={styles.small}>{t("console.patient.patientReads")}</p>
      <PatientLanguage locale={c.patientLanguage}>
        <PatientAgreement
          eligibility={form.eligibility}
          consent={form.consent}
          onEligibility={toggleEligibility}
          onConsent={(v) => {
            update({ consent: v });
            setExtra((x) => ({ ...x, consent: false }));
          }}
        />
      </PatientLanguage>
      {extra.eligibility ? <Banner tone="danger">{t("console.patient.eligibilityMissing")}</Banner> : null}
      {extra.consent ? <Banner tone="danger">{t("console.patient.consentMissing")}</Banner> : null}
      <ErrorBanner error={save.error} />
    </StepFrame>
  );
}

/** Anspruch (§ 2) und Einwilligung (§ 3) – in der Sprache der Patientin / des Patienten */
function PatientAgreement({
  eligibility,
  consent,
  onEligibility,
  onConsent,
}: {
  eligibility: EligibilityReason[];
  consent: boolean;
  onEligibility: (r: EligibilityReason, on: boolean) => void;
  onConsent: (v: boolean) => void;
}) {
  const t = useT();
  return (
    <>
      <fieldset className={styles.section}>
        <legend className={styles.sectionTitle}>{t("patient.eligibility.title")}</legend>
        {ELIGIBILITY_REASONS.map((r) => (
          <Checkbox key={r} card isSelected={eligibility.includes(r)} onChange={(v) => onEligibility(r, v)}>
            {t(`patient.eligibility.${r}`)}
          </Checkbox>
        ))}
      </fieldset>
      <div className={styles.section}>
        <span className={styles.sectionTitle}>{t("patient.consent.title")}</span>
        <Checkbox card isSelected={consent} onChange={onConsent}>
          {t("patient.consent.text")}
        </Checkbox>
      </div>
    </>
  );
}
